import type { FC, FormEvent, ReactNode } from 'react'
import type { CoverKey } from '@/types'

import Image from 'next/image'
import { useId, useState } from 'react'
import { Check, LoaderCircle, Plus, X } from 'lucide-react'
import { cn } from '@/lib/utils'
import { parseAmount } from '@/lib/money'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { FormField } from '@/components/trip/form-field'

import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue
} from '@/components/ui/select'

import { COVERS, CURRENCIES, LIMITS } from '@/constants/trip'

export type TripFormValues = {
    name: string
    destination: string
    startDate: string
    endDate: string
    currency: string
    budget: string
    cover: CoverKey
    travelers: string[]
}

/** Turns the budget field into minor units, or null when left empty. */
export const budgetToCents = (budget: string, currency: string): number | null => {
    if (!budget.trim()) return null
    return parseAmount(budget, currency)
}

interface TripFormProps {
    initial: TripFormValues
    withTravelers?: boolean
    currencyLocked?: boolean
    submitLabel: string
    onSubmit: (values: TripFormValues) => Promise<void>
    onCancel?: () => void
}

type Errors = Partial<Record<'name' | 'dates' | 'budget' | 'travelers' | 'form', string>>

const CURRENCY_ITEMS = CURRENCIES.map(currency => ({ value: currency.code, label: `${currency.code}, ${currency.name}` }))

const validate = (values: TripFormValues, withTravelers: boolean): Errors => {
    const errors: Errors = {}

    if (!values.name.trim()) errors.name = 'Give your trip a name.'

    if (values.startDate && values.endDate && values.endDate < values.startDate) {
        errors.dates = 'The trip ends before it starts.'
    }

    if (values.budget.trim()) {
        const cents = parseAmount(values.budget, values.currency)
        if (!cents || cents <= 0) errors.budget = 'Enter a budget above zero, or leave it empty.'
    }

    if (withTravelers) {
        const names = values.travelers.map(name => name.trim()).filter(Boolean)
        const lower = names.map(name => name.toLowerCase())

        if (names.length < 2) errors.travelers = 'Add at least two travelers.'
        else if (new Set(lower).size !== lower.length) errors.travelers = 'Each traveler needs a different name.'
    }

    return errors
}

export const TripForm: FC<TripFormProps> = ({
    initial,
    withTravelers = false,
    currencyLocked = false,
    submitLabel,
    onSubmit,
    onCancel
}): ReactNode => {
    const formId = useId()
    const [values, setValues] = useState<TripFormValues>(initial)
    const [errors, setErrors] = useState<Errors>({})
    const [submitting, setSubmitting] = useState(false)

    const update = <K extends keyof TripFormValues>(key: K, value: TripFormValues[K]) => {
        setValues(current => ({ ...current, [key]: value }))
    }

    const updateTraveler = (index: number, value: string) => {
        setValues(current => ({
            ...current,
            travelers: current.travelers.map((name, position) => (position === index ? value : name))
        }))
    }

    const addTravelerRow = () => {
        if (values.travelers.length >= LIMITS.travelers) return
        setValues(current => ({ ...current, travelers: [...current.travelers, ''] }))

        requestAnimationFrame(() => {
            document.getElementById(`${formId}-traveler-${values.travelers.length}`)?.focus()
        })
    }

    const removeTravelerRow = (index: number) => {
        setValues(current => ({ ...current, travelers: current.travelers.filter((_, position) => position !== index) }))
    }

    const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
        event.preventDefault()
        if (submitting) return

        const found = validate(values, withTravelers)
        setErrors(found)
        if (Object.keys(found).length > 0) return

        setSubmitting(true)

        try {
            await onSubmit({
                ...values,
                travelers: values.travelers.map(name => name.trim()).filter(Boolean)
            })
        }
        catch (error) {
            setErrors({ form: error instanceof Error ? error.message : 'Something went wrong. Please try again.' })
            setSubmitting(false)
            return
        }

        setSubmitting(false)
    }

    return (
        <form onSubmit={handleSubmit} noValidate className='flex flex-col gap-6'>
            <FormField id={`${formId}-name`} label='Trip name' error={errors.name}>
                <Input
                    id={`${formId}-name`}
                    value={values.name}
                    onChange={event => update('name', event.target.value)}
                    placeholder='Lisbon long weekend'
                    maxLength={LIMITS.tripName}
                    autoComplete='off'
                    aria-invalid={Boolean(errors.name) || undefined}
                    aria-describedby={errors.name ? `${formId}-name-error` : undefined}
                />
            </FormField>

            <FormField id={`${formId}-destination`} label='Destination' optional>
                <Input
                    id={`${formId}-destination`}
                    value={values.destination}
                    onChange={event => update('destination', event.target.value)}
                    placeholder='Lisbon, Portugal'
                    maxLength={LIMITS.destination}
                    autoComplete='off'
                />
            </FormField>

            <div className='grid gap-4 sm:grid-cols-2'>
                <FormField id={`${formId}-start`} label='Starts' optional>
                    <Input
                        id={`${formId}-start`}
                        type='date'
                        value={values.startDate}
                        onChange={event => update('startDate', event.target.value)}
                        aria-invalid={Boolean(errors.dates) || undefined}
                    />
                </FormField>

                <FormField id={`${formId}-end`} label='Ends' optional error={errors.dates}>
                    <Input
                        id={`${formId}-end`}
                        type='date'
                        value={values.endDate}
                        min={values.startDate || undefined}
                        onChange={event => update('endDate', event.target.value)}
                        aria-invalid={Boolean(errors.dates) || undefined}
                        aria-describedby={errors.dates ? `${formId}-end-error` : undefined}
                    />
                </FormField>
            </div>

            <FormField
                id={`${formId}-currency`}
                label='Currency'
                hint={currencyLocked ? 'The currency is fixed once expenses are added.' : 'Every expense on this trip uses this currency.'}
            >
                <Select
                    items={CURRENCY_ITEMS}
                    value={values.currency}
                    onValueChange={value => value && update('currency', value as string)}
                    disabled={currencyLocked}
                >
                    <SelectTrigger id={`${formId}-currency`}>
                        <SelectValue />
                    </SelectTrigger>

                    <SelectContent>
                        {CURRENCY_ITEMS.map(item => (
                            <SelectItem key={item.value} value={item.value}>
                                {item.label}
                            </SelectItem>
                        ))}
                    </SelectContent>
                </Select>
            </FormField>

            <FormField
                id={`${formId}-budget`}
                label={`Budget (${values.currency})`}
                optional
                error={errors.budget}
                hint='Tallyho shows how much of it the group has spent.'
            >
                <Input
                    id={`${formId}-budget`}
                    value={values.budget}
                    onChange={event => update('budget', event.target.value)}
                    placeholder='1,500'
                    inputMode='decimal'
                    autoComplete='off'
                    className='tabular-nums'
                    aria-invalid={Boolean(errors.budget) || undefined}
                    aria-describedby={errors.budget ? `${formId}-budget-error` : `${formId}-budget-hint`}
                />
            </FormField>

            <fieldset className='flex flex-col gap-3'>
                <legend className='mb-1 text-sm font-semibold'>
                    Cover photo
                </legend>

                <div role='radiogroup' aria-label='Cover photo' className='grid grid-cols-3 gap-2.5'>
                    {COVERS.map(cover => {
                        const selected = values.cover === cover.key

                        return (
                            <button
                                key={cover.key}
                                type='button'
                                role='radio'
                                aria-checked={selected}
                                onClick={() => update('cover', cover.key)}
                                className={cn(
                                    'group relative aspect-[4/3] overflow-hidden rounded-2xl outline-none ring-offset-2 ring-offset-card transition-all focus-visible:ring-3 focus-visible:ring-ring/60',
                                    selected ? 'ring-3 ring-primary' : 'ring-1 ring-border hover:ring-foreground/30'
                                )}
                            >
                                <Image
                                    src={cover.image}
                                    alt=''
                                    fill
                                    sizes='(min-width: 640px) 160px, 33vw'
                                    className='object-cover transition-transform duration-500 group-hover:scale-105 motion-reduce:transition-none'
                                />

                                <span aria-hidden='true' className='absolute inset-0 bg-linear-to-t from-foreground/70 via-transparent to-transparent' />

                                <span className='absolute inset-x-0 bottom-0 px-2 pb-1.5 text-left text-xs font-bold text-background'>
                                    {cover.label}
                                </span>

                                {selected && (
                                    <span className='absolute top-1.5 right-1.5 inline-flex size-6 items-center justify-center rounded-full bg-primary text-primary-foreground shadow'>
                                        <Check className='size-3.5' strokeWidth={3} aria-hidden='true' />
                                    </span>
                                )}
                            </button>
                        )
                    })}
                </div>
            </fieldset>

            {withTravelers && (
                <fieldset className='flex flex-col gap-3'>
                    <legend className='mb-1 text-sm font-semibold'>
                        Who is going?
                    </legend>

                    <ul className='flex flex-col gap-2.5'>
                        {values.travelers.map((name, index) => (
                            <li key={index} className='flex items-center gap-2'>
                                <label htmlFor={`${formId}-traveler-${index}`} className='sr-only'>
                                    {`Traveler ${index + 1}`}
                                </label>

                                <Input
                                    id={`${formId}-traveler-${index}`}
                                    value={name}
                                    onChange={event => updateTraveler(index, event.target.value)}
                                    placeholder={index === 0 ? 'Your name' : 'Friend’s name'}
                                    maxLength={LIMITS.travelerName}
                                    autoComplete='off'
                                    aria-invalid={Boolean(errors.travelers) || undefined}
                                />

                                <Button
                                    type='button'
                                    variant='ghost'
                                    size='icon'
                                    onClick={() => removeTravelerRow(index)}
                                    disabled={values.travelers.length <= 2}
                                    aria-label={`Remove traveler ${index + 1}`}
                                >
                                    <X className='size-4' />
                                </Button>
                            </li>
                        ))}
                    </ul>

                    {errors.travelers && (
                        <p role='alert' className='text-sm font-medium text-destructive'>
                            {errors.travelers}
                        </p>
                    )}

                    <Button
                        type='button'
                        variant='outline'
                        onClick={addTravelerRow}
                        disabled={values.travelers.length >= LIMITS.travelers}
                        className='self-start'
                    >
                        <Plus className='size-4' />
                        Add traveler
                    </Button>
                </fieldset>
            )}

            {errors.form && (
                <p role='alert' className='rounded-2xl bg-destructive/10 px-4 py-3 text-sm font-medium text-destructive'>
                    {errors.form}
                </p>
            )}

            <div className='flex flex-col-reverse gap-2 sm:flex-row sm:justify-end'>
                {onCancel && (
                    <Button type='button' variant='ghost' onClick={onCancel} disabled={submitting}>
                        Cancel
                    </Button>
                )}

                <Button type='submit' size='lg' disabled={submitting}>
                    {submitting && <LoaderCircle className='size-4 animate-spin' />}
                    {submitting ? 'Saving' : submitLabel}
                </Button>
            </div>
        </form>
    )
}
